import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { keycloakService, AuthUser } from '../services/keycloakService';
import { syncGobodoAuthToken } from '../gobodo/gobodoClient';
import { gobodoUserProvider } from '../services/providers/gobodoUserProvider';
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const updateUser = useCallback(() => {
    if (keycloakService.isAuthenticated()) {
      const userInfo = keycloakService.getUserInfo();
      setUser(userInfo);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Cypress E2E test bypass — dev only, never runs in production
        if (process.env.NODE_ENV !== 'production' && (window as any).Cypress) {
          setUser({
            id: 'cypress-test-user',
            email: 'magnusd@regalcredit.com',
            firstName: 'Magnus',
            lastName: 'D',
            roles: ['system_admin'],
            permissions: ['*'],
          });
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 Initializing authentication...');
          console.log('Current URL:', window.location.href);
        }

        const authenticated = await keycloakService.init();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 Authentication result:', authenticated);
        }
        
        setIsAuthenticated(authenticated);

        if (authenticated) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ User authenticated, updating user info...');
          }
          updateUser();
          // Sync token on initial auth
          syncGobodoAuthToken();
          // Handle post-sign-in tasks (update lastLogin and add to graph)
          handlePostSignIn().catch((err) => {
            console.warn('[AuthContext] Post-sign-in tasks failed:', err instanceof Error ? err.message : err);
          });
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('❌ User not authenticated');
          }
        }

        // Set up Keycloak event listeners
        keycloakService.onAuthSuccess(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Auth success event fired');
          }
          updateUser();
          // Sync token on auth success
          syncGobodoAuthToken();
          // Handle post-sign-in tasks (update lastLogin and add to graph)
          handlePostSignIn().catch((err) => {
            console.warn('[AuthContext] Post-sign-in tasks failed:', err instanceof Error ? err.message : err);
          });
        });

        keycloakService.onTokenExpired(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ Token expired, attempting refresh...');
          }
          // Token expired, try to refresh
          keycloakService.getKeycloak()?.updateToken(30).then(() => {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Token refreshed');
            }
            updateUser();
            // Sync refreshed token
            syncGobodoAuthToken();
          }).catch(() => {
            if (process.env.NODE_ENV === 'development') {
              console.log('❌ Token refresh failed, logging out');
            }
            // Refresh failed, logout
            setIsAuthenticated(false);
            setUser(null);
            // Clear token on refresh failure
            syncGobodoAuthToken();
          });
        });

        keycloakService.onAuthError(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('❌ Auth error event fired');
          }
          setIsAuthenticated(false);
          setUser(null);
          // Clear token on auth error
          syncGobodoAuthToken();
        });
      } catch (error) {
        console.error('❌ Auth initialization failed:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 Auth initialization complete');
        }
      }
    };

    initAuth();
  }, [updateUser]);

  /**
   * Handle post-sign-in tasks: auto-sync user to Gobodo, update last login, and add to graph
   * This ensures users are automatically created/updated in Gobodo when they log in via Keycloak
   */
  const handlePostSignIn = async () => {
    try {
      if (!keycloakService.isAuthenticated()) {
        return;
      }

      if (process.env.REACT_APP_USE_GOBODO_API === 'true') {
        // Defer until page queries finish, then run through the serial queue
        await new Promise(resolve => setTimeout(resolve, 8000));

        const keycloakUser = keycloakService.getUserInfo();
        if (!keycloakUser) {
          console.warn('[AuthContext] Cannot sync user: Keycloak user info not available');
          return;
        }

        try {
          let gobodoUser = await gobodoUserProvider.getSelf();
          
          if (!gobodoUser) {
            // User doesn't exist in Gobodo, create them
            if (process.env.NODE_ENV === 'development') {
              console.log('[AuthContext] User not found in Gobodo, creating new user...');
            }
            
            gobodoUser = await gobodoUserProvider.createUser({
              username: keycloakUser.email || keycloakUser.id,
              email: keycloakUser.email || '',
              firstName: keycloakUser.firstName || '',
              lastName: keycloakUser.lastName || '',
              department: keycloakUser.department,
              isActive: true,
              keycloakUserId: keycloakUser.id,
            });
            
            if (process.env.NODE_ENV === 'development') {
              console.log('[AuthContext] Successfully created user in Gobodo:', gobodoUser.id);
            }
          } else {
            // User exists, update with latest Keycloak info if needed
            const needsUpdate = 
              (keycloakUser.firstName && gobodoUser.firstName !== keycloakUser.firstName) ||
              (keycloakUser.lastName && gobodoUser.lastName !== keycloakUser.lastName) ||
              (keycloakUser.email && gobodoUser.email !== keycloakUser.email) ||
              (keycloakUser.department && gobodoUser.department !== keycloakUser.department);
            
            if (needsUpdate) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[AuthContext] Updating user info in Gobodo...');
              }
              
              await gobodoUserProvider.updateUser(gobodoUser.id, {
                firstName: keycloakUser.firstName || gobodoUser.firstName,
                lastName: keycloakUser.lastName || gobodoUser.lastName,
                email: keycloakUser.email || gobodoUser.email,
                department: keycloakUser.department || gobodoUser.department,
                keycloakUserId: keycloakUser.id,
              });
            }
          }

          // Pass the already-fetched userId to avoid redundant getSelf() calls
          // that would trigger Neo4j transaction collisions
          const userId = gobodoUser.id;

          // Update last login -- sequential to avoid Neo4j collisions
          try {
            const lastLogin = new Date().toISOString();
            await gobodoUserProvider.updateUser(userId, { lastLogin });
            console.log(`[GobodoUserProvider] Successfully updated lastLogin for user ${userId}`);
          } catch (loginErr) {
            console.warn('[AuthContext] Failed to update lastLogin:', loginErr);
          }
          
          // Add current user to graph
          try {
            await gobodoUserProvider.addUserToGraph(userId);
          } catch (graphErr) {
            console.warn('[AuthContext] Failed to add user to graph:', graphErr);
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[AuthContext] Successfully synced user, updated lastLogin, and added to graph');
          }
        } catch (error: any) {
          // Handle specific error cases
          if (error.status === 404 || error.message?.includes('not found')) {
            // User doesn't exist, try to create
            try {
              const newUser = await gobodoUserProvider.createUser({
                username: keycloakUser.email || keycloakUser.id,
                email: keycloakUser.email || '',
                firstName: keycloakUser.firstName || '',
                lastName: keycloakUser.lastName || '',
                department: keycloakUser.department,
                isActive: true,
                keycloakUserId: keycloakUser.id,
              });
              
              // Pass userId directly to avoid redundant getSelf() calls
              try {
                const lastLogin = new Date().toISOString();
                await gobodoUserProvider.updateUser(newUser.id, { lastLogin });
              } catch (loginErr) {
                console.warn('[AuthContext] Failed to update lastLogin for new user:', loginErr);
              }
              try {
                await gobodoUserProvider.addUserToGraph(newUser.id);
              } catch (graphErr) {
                console.warn('[AuthContext] Failed to add new user to graph:', graphErr);
              }
              
              if (process.env.NODE_ENV === 'development') {
                console.log('[AuthContext] Created user after 404 error:', newUser.id);
              }
            } catch (createError) {
              console.warn('[AuthContext] Failed to create user after 404:', createError);
            }
          } else {
            const msg = error instanceof Error ? error.message : (error?.message ?? JSON.stringify(error));
            console.warn('[AuthContext] Gobodo user sync failed:', msg);
          }
        }
      }
      // Note: In mock mode, auto-sync would need to be handled differently
      // since we don't have a current user ID readily available. This can be added later
      // if needed for mock mode testing.
    } catch (error) {
      // Don't fail sign-in if post-sign-in tasks fail
      console.warn('[AuthContext] Failed to complete post-sign-in tasks:', error);
    }
  };

  const login = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Login button clicked');
      }
      await keycloakService.login();
      // User will be redirected, so we don't need to update state here
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Login call completed, redirect should happen');
      }
    } catch (error) {
      console.error('❌ Login failed:', error);
      // Show error to user
      alert('Login failed. Please check the console for details.');
    }
  };

  const logout = async () => {
    try {
      // Clear local state first
      setUser(null);
      setIsAuthenticated(false);
      // Then logout from Keycloak (will redirect)
      await keycloakService.logout({ redirectUri: window.location.origin });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const hasRole = (role: string): boolean => {
    return keycloakService.hasRole(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return keycloakService.hasAnyRole(roles);
  };

  const refreshUser = () => {
    updateUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        hasRole,
        hasAnyRole,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

